import { SubscriptionAndPaymentDetailsResponse, SubscriptionCheckoutResponse } from 'generated';
import moment from 'moment';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  selectCurrentSubscription,
  selectCurrentSubscriptionCheckout,
} from 'redux/configuration/selector';
import useSchoolType from './useSchoolType';
import { SubscriptionPermission } from './useSubscriptionCheck';

const useSubscriptionDetail = () => {
  const currentSubscription = useSelector(
    selectCurrentSubscription,
  ) as SubscriptionAndPaymentDetailsResponse;
  const initResponse = useSelector(
    selectCurrentSubscriptionCheckout,
  ) as SubscriptionCheckoutResponse;

  const { isMadrasa } = useSchoolType();

  let isSubscriptionUser = !isMadrasa ? true : false;

  if (
    currentSubscription?.subscription?.id === null &&
    currentSubscription?.subscription?.is_blocked === false
  ) {
    isSubscriptionUser = false;
  }

  const subscriptionDetail = useMemo(() => {
    let isSubscriptionActive = false;
    let isSubscriptionTrial = false;
    let isSubscriptionGrace = false;
    let trialDiff = -1;
    let graceDiff = -1;
    let showWarning = false;
    let subscriptionMessage = 'Your Subscription plan is about to end.';

    if (isSubscriptionUser) {
      const now = moment();
      if (currentSubscription?.subscription?.is_trial) {
        const trialEnd = moment(currentSubscription?.subscription?.trial_expiry_date);
        if (trialEnd > now) {
          isSubscriptionActive = true;
          isSubscriptionTrial = true;
          trialDiff = trialEnd?.diff(now, 'days');
          if (trialDiff < 7) {
            showWarning = true;
            subscriptionMessage = `Your trial period is about to end on the ${trialEnd.format(
              'll',
            )}.`;
          }
        }
      } else {
        const subscriptionGraceEnd = moment(currentSubscription?.subscription?.grace_expiry_date);
        if (subscriptionGraceEnd > now) {
          isSubscriptionActive = true;
          isSubscriptionGrace = true;

          graceDiff = subscriptionGraceEnd.diff(now, 'days');
          if (graceDiff < 7) {
            showWarning = true;
            subscriptionMessage = `Your Subscription plan is about to end on the ${subscriptionGraceEnd.format(
              'll',
            )}.`;
          }
        }
      }
    }

    if ((initResponse?.balance || 0) > 0 && (initResponse?.total_amount_paid || 0) > 0) {
      isSubscriptionActive = true;
    }

    if (currentSubscription?.subscription?.is_blocked === false) {
      isSubscriptionActive = true;
    }

    if (currentSubscription?.subscription?.pricing_model === 'SPLIT') {
      isSubscriptionActive = true;
      isSubscriptionGrace = false;
    }

    if (
      currentSubscription?.subscription?.is_blocked ||
      (currentSubscription?.subscription?.subscription_plan?.id === 'FREE' &&
        (currentSubscription?.class_member_count || 0) > 50 &&
        currentSubscription?.subscription?.pricing_model !== 'SPLIT')
    ) {
      isSubscriptionGrace = false;
      isSubscriptionActive = false;
      isSubscriptionTrial = false;
    }

    return {
      isSubscriptionActive,
      isSubscriptionTrial,
      trialDiff,
      isSubscriptionGrace,
      graceDiff,
      showWarning,
      subscriptionMessage,
    };
  }, [
    currentSubscription?.class_member_count,
    currentSubscription?.subscription?.grace_expiry_date,
    currentSubscription?.subscription?.is_blocked,
    currentSubscription?.subscription?.is_trial,
    currentSubscription?.subscription?.pricing_model,
    currentSubscription?.subscription?.subscription_plan?.id,
    currentSubscription?.subscription?.trial_expiry_date,
    initResponse?.balance,
    initResponse?.total_amount_paid,
    isSubscriptionUser,
  ]);

  const checkSubscriptionPermission = (permission: SubscriptionPermission) => {
    if (
      currentSubscription?.subscription?.is_trial ||
      currentSubscription?.subscription?.subscription_plan?.features?.includes(permission) ||
      (!isSubscriptionUser && !currentSubscription?.subscription?.is_blocked)
    ) {
      return true;
    } else {
      return false;
    }
  };

  return {
    subscriptionDetail,
    isSubscriptionUser,
    checkSubscriptionPermission,
    initResponse: initResponse,
    currentSubscription: currentSubscription?.subscription,
    activeStudentCount: currentSubscription?.class_member_count || 0,
    amountPerStudent: currentSubscription?.amount_per_student || 0,
    termTotal: currentSubscription?.term_total || 0,
    sessionTotal: currentSubscription?.session_total || 0,
  };
};

export default useSubscriptionDetail;
